import { MantineProvider, v8CssVariablesResolver } from '@mantine/core';
import { temaMae } from './temaMae';
import { DARK_THEME_ENABLED } from '@/shared/lib/temaScuro';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { DatesProvider } from '@mantine/dates';
import { SpotlightProvider } from './SpotlightProvider';
import { AuthProvider } from './AuthProvider';
import { AbilityProvider } from './AbilityProvider';
import { QueryProvider } from './QueryProvider';
import { installJsonApiWarningNotifier } from '@/shared/lib/jsonApiWarningNotifier';
import { useEffect, type ReactNode } from 'react';

/* IBM Plex Sans + Mono, serviti da `public/fonts/plex/` (MaeDesign v3 §4.1, vedi `fonts.css`).
   Sta **prima** di Mantine perche' e' il layer che dichiara le facce: il tema qui sotto le
   nomina, non le carica. */
import '@/shared/styles/fonts.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/carousel/styles.css';
import '@mantine/tiptap/styles.css';
import 'mantine-datatable/styles.css';
import '@/shared/styles/maedesign-tokens.css';
/*
 * La palette MaeWay (`--*`, chiaro + scuro) deve stare **qui**, nel layer globale, e non
 * essere un effetto collaterale di chi la importa.
 *
 * Era importata solo da `MobileAppShell`, `PortalMobileShell` e `DesktopHome`: tre moduli
 * lazy. In dev non si vede, perché il grafo è unico e basta averla toccata una volta; nel
 * bundle no — il CSS di un chunk lazy arriva col chunk. Su una pagina desktop che non è la
 * Home le `--*` non erano definite, ogni token cadeva sul proprio fallback hex (cioè sul
 * valore **chiaro**) e il tema scuro tornava a non funzionare esattamente dove serviva.
 * Da lì la regola: le variabili si dichiarano una volta, sempre, prima di tutto.
 */
import { AppLockGate } from '@/shared/components/AppLockGate';
import { PreferencesProvider } from '@/shared/lib/PreferenzeProvider';
import { AnimatedSplash } from '@/shared/components/SplashAnimato';
import '@/shared/mobile/theme.css';
import '@/shared/styles/safe-area.css';
import '@/shared/styles/app-shell.css';

/**
 * Tema MaeDesign beta v1 (identità Maestrale): bordeaux primario, azzurro accento,
 * **IBM Plex Sans** per l'interfaccia e **IBM Plex Mono** per gli identificativi (v3 §4.1), raggi/ombre DS. La chiave colore resta `brand` così l'override di
 * branding per-tenant (AppShell → `--mantine-color-brand-6/-filled`) continua a
 * funzionare: bordeaux è il DEFAULT, non un lock. (Handoff §1/§3, theme.mae.ts.)
 */
export function AppProviders({ children }: { children: ReactNode }) {
  /* Gli avvisi su risposta riuscita (`meta.warnings`) li raccoglie il client JSON:API, ma
     finché nessuno registra la resa restano invisibili — ed è così che sono rimasti finora.
     Qui, non più in basso: deve essere attivo prima della prima scrittura. */
  useEffect(() => installJsonApiWarningNotifier(), []);

  return (
    <MantineProvider
      /*
       * ⚠️ `defaultColorScheme` e' **`light`**, non `auto`, ed e' la parte che conta di questa
       * riaccensione. `auto` segue il sistema operativo, ed e' **la ragione per cui il tema
       * scuro era stato sospeso**: chi aveva il Mac in scuro si trovava l'applicativo scuro
       * senza averlo scelto e senza sapere dove disattivarlo. Il commento che stava qui diceva
       * che `auto` «torna a valere da se il giorno che il flag ritorna true» — cioe' descriveva
       * come rassicurazione il ritorno del difetto.
       *
       * Con `light` lo scuro resta **opt-in**: lo si ha se lo si sceglie, dai due comandi (menu
       * utente e Impostazioni → Preferenze), e la scelta viaggia col profilo.
       *
       * `forceColorScheme` resta appeso al flag, cosi' sospenderlo di nuovo e' una riga.
       */
      theme={temaMae}
      /*
       * ⚠️ La rete di sicurezza dell'aggiornamento a Mantine 9, e la riga piu' importante di
       * questo file in questo momento.
       *
       * Nella 9 la variante `light` passa dalle trasparenze a colori pieni: sono **904
       * occorrenze in 280 file** — ogni `Badge` (che qui ha `variant: 'light'` come default),
       * ogni `Alert`, ogni `ThemeIcon`, ogni bottone chiaro. Non e' una rottura che il
       * compilatore veda ne' che un test possa cogliere: e' l'applicazione intera che cambia
       * tinta in un colpo.
       *
       * `v8CssVariablesResolver` rimette le trasparenze di prima — `alpha(colore, .1)`, hover
       * `.12`, outline `.05` — cioe' **esattamente l'aspetto di oggi**. Il cambio dei colori
       * `light` e' arrivato con la 9, quindi la 7 e la 8 sono uguali su questo punto e il
       * risolutore «v8» vale anche per noi che veniamo dalla 7.
       *
       * Toglierlo e' una decisione di grafica da prendere a parte, guardando le pagine. Finche'
       * c'e', l'aggiornamento non sposta un colore.
       */
      cssVariablesResolver={v8CssVariablesResolver}
      defaultColorScheme="light"
      forceColorScheme={DARK_THEME_ENABLED ? undefined : 'light'}
    >
      <DatesProvider settings={{ locale: 'it', firstDayOfWeek: 1, weekendDays: [0, 6] }}>
        <Notifications position="top-right" />
        <QueryProvider>
          <AuthProvider>
            {/* Le preferenze della persona, lette **una volta sola** e prima di disegnare: e' cio'
                che permette di non tenerne una copia sul dispositivo. Sta dentro `AuthProvider`
                perche' gli serve l'id dal token, e sopra tutto il resto perche' sotto ci sono le
                cose che quei valori li usano per decidere come apparire. */}
            <PreferencesProvider>
            <AbilityProvider>
              <ModalsProvider>
                {/* Il blocco biometrico avvolge l'applicazione **senza smontarla**: chi rientra
                    dopo un minuto trova il velo davanti e, sbloccato, la pagina esattamente dov'era.
                    Sul web e con il blocco spento e' trasparente: rende i figli e nient'altro. */}
                <AppLockGate>
                  <SpotlightProvider>{children}</SpotlightProvider>
                </AppLockGate>
                {/* Sopra tutto e per pochi istanti: raccoglie il testimone dallo splash di sistema
                    e lo passa all'app. Sul web non rende niente. */}
                <AnimatedSplash />
              </ModalsProvider>
            </AbilityProvider>
            </PreferencesProvider>
          </AuthProvider>
        </QueryProvider>
      </DatesProvider>
    </MantineProvider>
  );
}
